import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';

import {
  CheckCircleOutline,
  Inventory2Outlined,
  LocalShippingOutlined,
  HighlightOffOutlined,
  RemoveCircleOutlineOutlined,
  RemoveCircleOutline,
  MedicalServices,
  Search,
  FileDownload,
  FileUpload,
} from '@mui/icons-material';

import { DataGrid } from '@mui/x-data-grid';

export default function Table({ auth, table, statusOptions, handleExport, handleImport }) {
  const [status, setStatus] = useState('all');
  const [searchText, setSearchText] = useState('');

  const filters = [
    {
      value: 'all',
      label: 'All',
      count: table?.details?.total_status?.all ?? 0,
      icon: null,
      color: '#112012',
      bg: '#ECEFF1',
    },
    {
      value: 'pending',
      label: 'No status yet',
      count: table?.details?.total_status?.pending ?? 0,
      icon: <RemoveCircleOutlineOutlined fontSize='small' />,
      color: '#90A4AE',
      bg: '#ECEFF1',
    },
    {
      value: 'preparing',
      label: 'Preparing',
      count: table?.details?.total_status?.preparing ?? 0,
      icon: <Inventory2Outlined fontSize='small' />,
      color: '#FB8C00',
      bg: '#FFF3E0',
    },
    {
      value: 'delivery',
      label: 'For delivery',
      count: table?.details?.total_status?.delivery ?? 0,
      icon: <LocalShippingOutlined fontSize='small' />,
      color: '#1E88E5',
      bg: '#E3F2FD',
    },
    {
      value: 'delivered',
      label: 'Delivered',
      count: table?.details?.total_status?.delivered ?? 0,
      icon: <CheckCircleOutline fontSize='small' />,
      color: '#43A047',
      bg: '#E8F5E9',
    },
    {
      value: 'cancelled',
      label: 'Cancelled',
      count: table?.details?.total_status?.cancelled ?? 0,
      icon: <HighlightOffOutlined fontSize='small' />,
      color: '#E53935',
      bg: '#FFEBEE',
    },
  ];

  const rows = useMemo(() => {
    return (table?.items ?? []).map((item) => ({
      ...item,
      id: item.id,
    }));
  }, [table?.items]);

  const filteredRows = useMemo(() => {
    let result = rows;

    // Status filter
    if (status !== 'all') {
      const statusMap = {
        pending: 1,
        preparing: 2,
        delivery: 3,
        delivered: 4,
        cancelled: 5,
      };

      if (status === 'pending') {
        result = result.filter((row) => row.status_id === null || row.status_id === 1);
      } else {
        result = result.filter((row) => row.status_id === statusMap[status]);
      }
    }

    // Medical Representative search
    if (searchText.trim()) {
      const search = searchText.trim().toLowerCase();

      result = result.filter((row) =>
        `${row.rep_name ?? ''} ${row.rep_id ?? ''}`.toLowerCase().includes(search),
      );
    }

    return result;
  }, [rows, status, searchText]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: 'medrep',
        headerName: 'Medical Representative',
        width: 250,
        minWidth: 250,
        maxWidth: 250,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack
            direction='row'
            spacing={1.5}
            alignItems='center'
            sx={{
              height: '100%',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                fontWeight={600}
                noWrap
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {row.rep_name}
              </Typography>

              <Typography variant='caption' color='text.secondary' noWrap>
                {row.rep_id}
              </Typography>
            </Box>
          </Stack>
        ),
      },

      {
        field: 'mds',
        headerName: 'MDS',
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            size='small'
            icon={<MedicalServices color='success' />}
            label={row.total_mds ?? 0}
            sx={{
              padding: 2,
              bgcolor: '#E8F5E9',
              color: '#2E7D32',
              fontWeight: 600,
            }}
          />
        ),
      },

      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        minWidth: 140,
        maxWidth: 140,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: ({ row }) => {
          const config = statusOptions.find((item) => item.value === row.status_id);

          if (!config) {
            return (
              <Chip
                size='small'
                icon={<RemoveCircleOutline />}
                label='No status yet'
                sx={{
                  bgcolor: '#78909C15',
                  color: '#78909C',
                  fontWeight: 600,
                }}
              />
            );
          }

          return (
            <Chip
              size='small'
              icon={config.icon}
              label={config.label}
              sx={{
                bgcolor: `${config.color}15`,
                color: config.color,
                fontWeight: 600,
              }}
            />
          );
        },
      },
    ];

    const productColumns = (table?.productColumn ?? []).map((product) => ({
      field: `product_${product.product_id}`,
      headerName: product.product_name,

      // IMPORTANT: no width here
      flex: 1,
      minWidth: 100,

      align: 'center',
      headerAlign: 'center',
      sortable: false,

      renderHeader: () => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            lineHeight: 1.1,
            px: 0,
          }}
        >
          <Typography
            fontSize={12}
            fontWeight={700}
            sx={{
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word',
            }}
          >
            {product.product_name}
          </Typography>

          <Typography
            variant='caption'
            color='text.secondary'
            sx={{
              lineHeight: 1.1,
            }}
          >
            {product.product_code}
          </Typography>
        </Box>
      ),

      renderCell: ({ row }) => {
        const allocation = row.products?.find(
          (item) => Number(item.product_id) === Number(product.product_id),
        );

        const value = allocation?.allocated ?? 0;

        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              margin: 0,
              padding: 0,

              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',

              bgcolor: value > 0 ? '#E8F5E9' : '#F5F5F5',
              color: value > 0 ? '#2E7D32' : '#9E9E9E',

              fontWeight: 700,
            }}
          >
            {value}
          </Box>
        );
      },
    }));

    return [...baseColumns, ...productColumns];
  }, [table?.productColumn, statusOptions]);

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box p={3}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography fontWeight={700} fontSize={20}>
              Plan Information
            </Typography>

            <Typography color='text.secondary' mt={0.5}>
              {table?.details?.plan_name ?? ''}
            </Typography>
          </Box>
          {!auth && (
            <Box display='flex' alignItems='center' gap={1}>
              <Button
                color='success'
                variant='contained'
                startIcon={<FileUpload />}
                onClick={() => handleImport()}
              >
                Import Delivery Status
              </Button>

              <Button
                color='primary'
                variant='contained'
                startIcon={<FileDownload />}
                onClick={() => handleExport()}
              >
                Export Delivery Status
              </Button>
            </Box>
          )}
        </Box>
        <Divider sx={{ mx: -3, my: 2 }} />

        <Stack direction='row' spacing={6} flexWrap='wrap'>
          <Box>
            <Typography variant='caption' color='text.secondary'>
              Plan Number
            </Typography>

            <Typography fontWeight={600}>{table?.details?.plan_id ?? '—'}</Typography>
          </Box>

          <Box>
            <Typography variant='caption' color='text.secondary'>
              Category
            </Typography>

            <br />

            {table?.details?.category === 'Regular' ? (
              <Chip
                size='small'
                label='● Regular'
                sx={{
                  bgcolor: '#E8F5E9',
                  color: '#2E7D32',
                  fontWeight: 600,
                }}
              />
            ) : (
              <Chip
                size='small'
                label={`● Special ${table?.details?.sub_category ?? ''}`}
                sx={{
                  bgcolor: '#FFF3E0',
                  color: '#EF6C00',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>

          <Box>
            <Typography variant='caption' color='text.secondary'>
              Number of Medreps
            </Typography>

            <Typography fontWeight={600}>{table?.details?.total_reps ?? 0}</Typography>
          </Box>

          <Box>
            <Typography variant='caption' color='text.secondary'>
              Total MDs
            </Typography>

            <Typography fontWeight={600}>{table?.details?.total_mds ?? 0}</Typography>
          </Box>

          <Box>
            <Typography variant='caption' color='text.secondary'>
              Total Qty
            </Typography>

            <Typography fontWeight={600}>{table?.details?.total_product_pcs ?? 0} pcs</Typography>
          </Box>

          <Box>
            <Typography variant='caption' color='text.secondary'>
              Effectivity
            </Typography>

            <Typography fontWeight={600}>
              {table?.details?.effectivity_month} {table?.details?.effectivity_year}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider />

      {/* Status Filter */}
      <Box px={3} py={2}>
        <Stack
          direction='row'
          spacing={1}
          sx={{
            overflowX: 'auto',
          }}
        >
          <Box>
            <TextField
              size='small'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder='Search medical representative'
              sx={{
                width: 280,
                flexShrink: 0,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search fontSize='small' />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {filters.map((item) => (
            <Chip
              key={item.value}
              clickable
              icon={item.icon}
              onClick={() => setStatus(item.value)}
              label={
                <Stack direction='row' spacing={0.7} alignItems='center'>
                  <span>{item.label}</span>

                  <Box
                    sx={{
                      minWidth: 18,
                      height: 18,
                      px: 0.5,
                      borderRadius: '999px',
                      bgcolor: 'rgba(255,255,255,.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {item.count}
                  </Box>
                </Stack>
              }
              sx={{
                height: 34,
                px: 1,
                borderRadius: 20,
                fontWeight: 600,
                bgcolor: status === item.value ? item.bg : `${item.color}12`,
                color: item.color,
                border: status === item.value ? `1px solid ${item.color}` : '1px solid transparent',
                opacity: status === item.value ? 1 : 0.65,

                '& .MuiChip-icon': {
                  color: item.color,
                },

                '&:hover': {
                  opacity: 1,
                  bgcolor: item.bg,
                },
              }}
            />
          ))}
        </Stack>
      </Box>

      <Divider />

      {/* DataGrid */}
      <Box
        sx={{
          border: 0,
          height: 500,
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: '#F8F9FB',
            borderBottom: '1px solid #E5E7EB',
            fontWeight: 700,
          },

          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
          },

          // REMOVE DEFAULT CELL PADDING
          '& .MuiDataGrid-cell': {
            padding: 0,
            margin: 0,
            borderBottom: '1px solid #F1F3F5',
            display: 'flex',
            alignItems: 'stretch',
          },

          // Product cell should have zero spacing
          '& .MuiDataGrid-cell[data-field^="product_"]': {
            padding: 0,
            margin: 0,
          },

          '& .MuiDataGrid-row:hover': {
            bgcolor: '#FAFAFA',
          },
        }}
      >
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={table?.loading}
          disableRowSelectionOnClick
          checkboxSelection={false}
          hideFooter
          rowHeight={72}
          sx={{
            border: 0,

            '& .MuiDataGrid-columnHeaders': {
              bgcolor: '#F8F9FB',
              borderBottom: '1px solid #E5E7EB',
              fontWeight: 700,
            },

            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
            },

            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #F1F3F5',
              display: 'flex',
              alignItems: 'center',
            },

            '& .MuiDataGrid-row:hover': {
              bgcolor: '#FAFAFA',
            },
          }}
        />
      </Box>

      {/* Total Allocation */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fafafa',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            width: '100%',
          }}
        >
          {/* Medical Representative */}
          <Box
            sx={{
              width: 250,
              minWidth: 250,
              maxWidth: 250,
              px: 2,
              py: 2,
              boxSizing: 'border-box',
            }}
          >
            <Typography fontWeight={700} fontSize={16}>
              Total Allocation
            </Typography>
          </Box>

          {/* MDS */}
          <Box
            sx={{
              width: 100,
              minWidth: 100,
              maxWidth: 100,
              flexShrink: 0,
            }}
          />

          {/* Status */}
          <Box
            sx={{
              width: 140,
              minWidth: 140,
              maxWidth: 140,
              flexShrink: 0,
            }}
          />

          {/* Products */}
          {(table?.productColumn ?? []).map((product) => {
            const total = (table?.items ?? []).reduce((sum, row) => {
              const allocation = row.products?.find(
                (item) => Number(item.product_id) === Number(product.product_id),
              );

              return sum + (allocation?.allocated ?? 0);
            }, 0);

            return (
              <Box
                key={product.product_id}
                sx={{
                  flex: 1,
                  minWidth: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 2,
                  boxSizing: 'border-box',
                }}
              >
                <Typography fontWeight={700} color={total > 0 ? 'success.main' : 'text.secondary'}>
                  {total}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
}

Table.propTypes = {
  auth: PropTypes.any,
  table: PropTypes.shape({
    loading: PropTypes.bool,
    show: PropTypes.bool,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        rep_id: PropTypes.string,
        rep_name: PropTypes.string,
        total_mds: PropTypes.number,
        status_id: PropTypes.number,
        status: PropTypes.string,
        products: PropTypes.arrayOf(
          PropTypes.shape({
            product_id: PropTypes.number.isRequired,
            allocated: PropTypes.number,
          }),
        ),
      }),
    ),
    details: PropTypes.shape({
      plan_id: PropTypes.string,
      plan_name: PropTypes.string,
      category_id: PropTypes.number,
      category: PropTypes.string,
      sub_category_id: PropTypes.number,
      sub_category: PropTypes.string,
      effectivity_month: PropTypes.string,
      effectivity_year: PropTypes.number,
      total_reps: PropTypes.number,
      total_mds: PropTypes.number,
      total_product_pcs: PropTypes.number,
      total_status: PropTypes.shape({
        all: PropTypes.number,
        pending: PropTypes.number,
        preparing: PropTypes.number,
        delivery: PropTypes.number,
        delivered: PropTypes.number,
        cancelled: PropTypes.number,
      }),
    }),
    productColumn: PropTypes.arrayOf(
      PropTypes.shape({
        product_id: PropTypes.number.isRequired,
        product_name: PropTypes.string,
        product_code: PropTypes.string,
        moe: PropTypes.string,
      }),
    ),
  }).isRequired,
  statusOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
      color: PropTypes.string,
      icon: PropTypes.element,
    }),
  ),
  handleExport: PropTypes.func,
  handleImport: PropTypes.func,
};
